import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Globe, Calendar, Clock, Ruler } from 'lucide-react';
import { 
  getSettings, 
  updateSettings, 
  resetSettingsToDefaults,
  TIMEZONE_OPTIONS,
  DATE_FORMAT_OPTIONS,
  WEEK_START_OPTIONS,
  MEASUREMENT_SYSTEM_OPTIONS,
  type AppSettings
} from '../config/settings';

interface SettingsPanelProps {
  onSettingsChange?: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ onSettingsChange }) => {
  const [settings, setSettings] = useState<AppSettings>(getSettings());
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    const currentSettings = getSettings();
    setSettings(currentSettings);
  }, []);

  const handleChange = (section: keyof AppSettings, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasChanges(true);
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    
    try {
      // Simulate async save operation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      updateSettings(settings);
      setHasChanges(false);
      setSaveStatus('saved');
      
      if (onSettingsChange) {
        onSettingsChange();
      }
      
      // Clear saved status after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      resetSettingsToDefaults();
      const defaultSettings = getSettings();
      setSettings(defaultSettings);
      setHasChanges(false);
      setSaveStatus('idle');
      
      if (onSettingsChange) {
        onSettingsChange();
      }
    }
  };

  const getSaveButtonText = () => {
    switch (saveStatus) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return 'Saved!';
      case 'error':
        return 'Error!';
      default:
        return 'Save Changes';
    }
  };

  const getSaveButtonClass = () => {
    const baseClass = "inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500";
    
    switch (saveStatus) {
      case 'saving':
        return `${baseClass} text-white bg-blue-400 cursor-not-allowed`;
      case 'saved':
        return `${baseClass} text-white bg-green-600 hover:bg-green-700`;
      case 'error':
        return `${baseClass} text-white bg-red-600 hover:bg-red-700`;
      default:
        return `${baseClass} text-white ${hasChanges ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Application Settings</h2>
        <p className="mt-2 text-gray-600">Configure your preferences for timezone, date format, and measurements</p>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Timezone & Location */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Globe className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Timezone & Location</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <select
                value={settings.timezone.ianaTz}
                onChange={(e) => {
                  const selectedTz = TIMEZONE_OPTIONS.find(tz => tz.value === e.target.value);
                  if (selectedTz) {
                    handleChange('timezone', 'ianaTz', selectedTz.value);
                    handleChange('timezone', 'name', selectedTz.value.split('/')[1] || selectedTz.value);
                    handleChange('timezone', 'displayName', selectedTz.label);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {TIMEZONE_OPTIONS.map(timezone => (
                  <option key={timezone.value} value={timezone.value}>
                    {timezone.label} ({timezone.offset})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Time
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
                {new Date().toLocaleString(settings.locale.numberFormat, {
                  timeZone: settings.timezone.ianaTz,
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: settings.timeFormat.hours === 12
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Date & Time Format */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Calendar className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Date & Time Format</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Format
              </label>
              <select
                value={settings.dateFormat.style}
                onChange={(e) => handleChange('dateFormat', 'style', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {DATE_FORMAT_OPTIONS.map(format => (
                  <option key={format.value} value={format.value}>
                    {format.label} - {format.example}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Format
              </label>
              <select
                value={settings.timeFormat.hours}
                onChange={(e) => handleChange('timeFormat', 'hours', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={24}>24-hour (14:30)</option>
                <option value={12}>12-hour (2:30 PM)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Week Starts On
              </label>
              <select
                value={settings.calendar.weekStartsOn}
                onChange={(e) => handleChange('calendar', 'weekStartsOn', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {WEEK_START_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Measurement System */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Ruler className="w-5 h-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Measurement System</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                System
              </label>
              <select
                value={settings.measurements.system}
                onChange={(e) => {
                  const system = e.target.value as 'metric' | 'imperial';
                  handleChange('measurements', 'system', system);
                  // Auto-update related fields
                  if (system === 'metric') {
                    handleChange('measurements', 'temperature', 'celsius');
                    handleChange('measurements', 'distance', 'kilometers');
                    handleChange('measurements', 'weight', 'kilograms');
                  } else {
                    handleChange('measurements', 'temperature', 'fahrenheit');
                    handleChange('measurements', 'distance', 'miles');
                    handleChange('measurements', 'weight', 'pounds');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {MEASUREMENT_SYSTEM_OPTIONS.map(system => (
                  <option key={system.value} value={system.value}>
                    {system.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                value={settings.measurements.currency}
                onChange={(e) => handleChange('measurements', 'currency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="USD">US Dollar (USD)</option>
                <option value="EUR">Euro (EUR)</option>
                <option value="GBP">British Pound (GBP)</option>
                <option value="NOK">Norwegian Krone (NOK)</option>
                <option value="KES">Kenyan Shilling (KES)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Display Examples */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Clock className="w-5 h-5 text-orange-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Preview</h3>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Current Date:</span>
              <span className="font-mono">
                {new Date().toLocaleDateString(settings.locale.numberFormat, {
                  timeZone: settings.timezone.ianaTz,
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Current Time:</span>
              <span className="font-mono">
                {new Date().toLocaleTimeString(settings.locale.numberFormat, {
                  timeZone: settings.timezone.ianaTz,
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: settings.timeFormat.hours === 12
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Week Starts:</span>
              <span>{WEEK_START_OPTIONS.find(w => w.value === settings.calendar.weekStartsOn)?.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">System:</span>
              <span>{settings.measurements.system === 'metric' ? 'Metric' : 'Imperial'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Currency:</span>
              <span>{settings.measurements.currency}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <button
          onClick={handleReset}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset to Defaults
        </button>
        
        <button
          onClick={handleSave}
          disabled={!hasChanges || saveStatus === 'saving'}
          className={getSaveButtonClass()}
        >
          <Save className="w-4 h-4 mr-2" />
          {getSaveButtonText()}
        </button>
      </div>

      {/* Save Status Message */}
      {saveStatus === 'saved' && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="text-green-800 text-sm">
            Settings saved successfully! Changes will take effect immediately.
          </div>
        </div>
      )}
      
      {saveStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="text-red-800 text-sm">
            Failed to save settings. Please try again.
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel; 